import { Dispatch, ReactNode, SetStateAction } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { POPULAR_LANGUAGES } from "../constants/languages";
import { VisualStyleId } from "../constants/visualStyles";
import { BrandKit } from "../models/BrandKit";
import { PlatformConnection } from "../models/Platform";
import { cn } from "../lib/utils";
import { useIsMobile } from "../hooks/useIsMobile";
import { BrandKitSelector } from "./BrandKitSelector";
import { VisualStyleSelector } from "./VisualStyleSelector";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { TemplateSelector } from "./TemplateSelector";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "./ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

interface Props {
  userBrandKits: BrandKit[];
  brandKitNotice?: ReactNode;
  sourceMayHaveImages?: boolean;
  selectedBrandKitId: string | null;
  setSelectedBrandKitId: (id: string | null) => void;
  selectedVisualStyleId: VisualStyleId;
  setSelectedVisualStyleId: (id: VisualStyleId) => void;
  targetLanguage?: string;
  setTargetLanguage?: (language: string) => void;
  /** Only needed when the accounts picker is shown. */
  activeConnections?: PlatformConnection[];
  selectedConnectionIds?: string[];
  setSelectedConnectionIds?: Dispatch<SetStateAction<string[]>>;
  selectedTemplateIds: Set<string>;
  setSelectedTemplateIds: (ids: Set<string>) => void;
  setSelectedTemplateLabel?: (label: string) => void;
  templateCategory?: "listicle" | "quote";
  /** Hide the accounts picker where choosing destinations would confuse. */
  showPostingTo?: boolean;
  showManageBrandKits?: boolean;
  /** The selected kit is applied but not in the list yet. */
  brandKitPending?: boolean;
  /** Shown instead of the picker when the workspace has no brand kit yet. */
  noBrandKitNote?: string;
}

export function GenerationSettings({
  userBrandKits,
  brandKitNotice,
  sourceMayHaveImages = false,
  selectedBrandKitId,
  setSelectedBrandKitId,
  selectedVisualStyleId,
  setSelectedVisualStyleId,
  targetLanguage = "auto",
  setTargetLanguage,
  activeConnections = [],
  selectedConnectionIds = [],
  setSelectedConnectionIds = () => {},
  selectedTemplateIds,
  setSelectedTemplateIds,
  setSelectedTemplateLabel,
  templateCategory,
  showPostingTo = true,
  showManageBrandKits = true,
  brandKitPending = false,
  noBrandKitNote,
}: Props) {
  const isMobile = useIsMobile();
  const selectedBrandKit = userBrandKits.find(
    (kit) => kit.id === selectedBrandKitId,
  );
  const selectedLanguageLabel =
    POPULAR_LANGUAGES.find((l) => l.value === targetLanguage)?.label ?? "Auto";
  return (
    <section className="mt-10 space-y-6">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Settings
      </p>

      {/* Brand Kit row */}
      <div className="space-y-4">
        {brandKitNotice}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Brand Kit</p>
            {noBrandKitNote && userBrandKits.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                {noBrandKitNote}
              </p>
            ) : (
              <p className="hidden text-xs text-muted-foreground mt-0.5 sm:block">
                Apply consistent fonts, colors, and logos to every post.
              </p>
            )}
          </div>
          {!(noBrandKitNote && userBrandKits.length === 0) && (
            <div className="shrink-0">
              <BrandKitSelector
                brandKits={userBrandKits}
                selectedKitId={selectedBrandKitId}
                onChange={setSelectedBrandKitId}
                showManage={showManageBrandKits}
                pending={brandKitPending}
              />
            </div>
          )}
        </div>
      </div>

      <VisualStyleSelector
        brandKit={selectedBrandKit}
        sourceMayHaveImages={sourceMayHaveImages}
        selectedStyleId={selectedVisualStyleId}
        onChange={setSelectedVisualStyleId}
      />

      {setTargetLanguage && (
        <>
          {/* Output Language row */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                Output Language
              </p>
              {/* The picker already reads "Auto (Same as source)". */}
              <p className="hidden text-xs text-muted-foreground mt-0.5 sm:block">
                Leave on Auto to match the source language.
              </p>
            </div>
            <div className="shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 px-3 text-sm font-normal"
                  >
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium text-foreground">
                      {selectedLanguageLabel}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5",
                        targetLanguage !== "auto"
                          ? "text-brand"
                          : "text-muted-foreground",
                      )}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 max-h-72 overflow-auto"
                >
                  {POPULAR_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.value}
                      onSelect={() => setTargetLanguage(lang.value)}
                    >
                      <div className="flex justify-between items-center gap-2 w-full">
                        <span>{lang.label}</span>
                        {lang.value === targetLanguage && (
                          <>
                            <Check
                              className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Selected</span>
                          </>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </>
      )}
      {/* Posting To row */}
      {showPostingTo && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Posting To</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {activeConnections.length === 0
                ? "No posts go out without your approval."
                : "Posts will be ready to publish to these accounts."}
            </p>
          </div>
          <div className="shrink-0">
            {activeConnections.length === 0 ? (
              <a
                href="/settings"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Connect an account
                <span aria-hidden>→</span>
              </a>
            ) : (
              (() => {
                const triggerButton = (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-2 pl-2 pr-2.5 text-sm font-normal"
                  >
                    {selectedConnectionIds.length > 0 && (
                      <span className="flex -space-x-1.5">
                        {activeConnections.slice(0, 4).map((conn) => {
                          const selected = selectedConnectionIds.includes(
                            conn.id,
                          );
                          return (
                            <span
                              key={conn.id}
                              className={cn(
                                "ring-2 ring-background rounded-full transition-opacity",
                                selected ? "opacity-100" : "opacity-25",
                              )}
                            >
                              <PlatformConnectionIcon
                                platform={conn.platform}
                                profileImage={conn.profileImage}
                                profileName={conn.profileName}
                                size="sm"
                                disableTooltip
                              />
                            </span>
                          );
                        })}
                        {activeConnections.length > 4 && (
                          <span className="ring-2 ring-background rounded-full w-6 h-6 bg-muted text-[10px] font-medium flex items-center justify-center text-muted-foreground">
                            +{activeConnections.length - 4}
                          </span>
                        )}
                      </span>
                    )}
                    <span className="font-medium text-foreground">
                      {selectedConnectionIds.length === 0
                        ? "None selected"
                        : selectedConnectionIds.length ===
                            activeConnections.length
                          ? activeConnections.length === 1
                            ? activeConnections[0].profileName
                            : `All ${activeConnections.length}`
                          : `${selectedConnectionIds.length} of ${activeConnections.length}`}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                );

                const headerRow = (
                  <div className="flex items-center justify-between px-2 pt-1 pb-3 md:pb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Accounts
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedConnectionIds(
                          selectedConnectionIds.length ===
                            activeConnections.length
                            ? []
                            : activeConnections.map((c) => c.id),
                        );
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      {selectedConnectionIds.length === activeConnections.length
                        ? "Clear"
                        : "Select all"}
                    </button>
                  </div>
                );

                const list = activeConnections.map((conn) => {
                  const selected = selectedConnectionIds.includes(conn.id);
                  return (
                    <button
                      key={conn.id}
                      type="button"
                      onClick={() => {
                        setSelectedConnectionIds((prev) =>
                          selected
                            ? prev.filter((id) => id !== conn.id)
                            : [...prev, conn.id],
                        );
                      }}
                      className={cn(
                        "group w-full flex items-center gap-3 px-2 py-3 md:py-1.5 md:gap-2.5 rounded-md text-left transition-colors",
                        "hover:bg-muted/60 active:bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "transition-opacity",
                          selected ? "opacity-100" : "opacity-40",
                        )}
                      >
                        <PlatformConnectionIcon
                          platform={conn.platform}
                          profileImage={conn.profileImage}
                          profileName={conn.profileName}
                          size="sm"
                          disableTooltip
                        />
                      </span>
                      <span
                        className={cn(
                          "flex-1 min-w-0 text-sm truncate",
                          selected
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {conn.profileName}
                      </span>
                      {selected ? (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <span className="h-4 w-4 shrink-0 rounded-sm border border-border group-hover:border-foreground/30 transition-colors" />
                      )}
                    </button>
                  );
                });

                if (isMobile) {
                  return (
                    <Drawer>
                      <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
                      <DrawerContent className="px-2 pb-6">
                        <DrawerTitle className="sr-only">
                          Posting accounts
                        </DrawerTitle>
                        <div className="pt-2">
                          {headerRow}
                          <div className="max-h-[60vh] overflow-auto">
                            {list}
                          </div>
                        </div>
                      </DrawerContent>
                    </Drawer>
                  );
                }

                return (
                  <Popover>
                    <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
                    <PopoverContent
                      align="end"
                      className="w-72 p-1.5"
                      sideOffset={6}
                    >
                      {headerRow}
                      <div className="max-h-72 overflow-auto">{list}</div>
                    </PopoverContent>
                  </Popover>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* Templates */}
      <TemplateSelector
        category={templateCategory}
        onSelectedLabelChange={setSelectedTemplateLabel}
        selectedTemplateIds={selectedTemplateIds}
        onChange={setSelectedTemplateIds}
        brandKitAllowedTemplateIds={selectedBrandKit?.allowedTemplateIds}
        brandKitName={selectedBrandKit?.name}
      />
    </section>
  );
}
